import { Grid } from '../../core/Grid.js';
import {
    Position,
    Direction,
    positionKey,
    positionsEqual,
    manhattanDistance,
    oppositeDir
} from '../../core/GridGeometry.js';
import { ScoreBreakdown, calculateScore } from '../../core/Scoring.js';
import type { ScoringHighlight } from '../scoringRules.js';
import { stripSeparators } from '../stateSerialization.js';

const ALL_DIRECTIONS: Direction[] = ['N', 'E', 'S', 'W'];

/**
 * A ramp: an ordered path of cells inside a single quarter.
 * `path[0]` is the **landing** (the only cell that may sit on a digit),
 * `path[last]` is the **launcher**, everything between is body.
 */
export interface Ramp {
    id: number;
    path: Position[];
    jumpDirection: Direction;
}

export type JumpColor = 'teal' | 'amber' | 'red';

/** Which quarter a cell is in: (0,0) = NW, (0,1) = NE, (1,0) = SW, (1,1) = SE. */
export interface Quarter {
    qr: 0 | 1;
    qc: 0 | 1;
}

/**
 * The jumper's walk from the landing on the lowest digit, following each launcher to
 * the landing it targets. Everything the board is coloured by comes from here.
 */
export interface JumpChain {
    /** Ramp ids in visit order, starting at the ramp landing on `minDigit`. */
    visited: number[];
    /** Digits landed on, in order, while the walk was still correct. */
    clearedDigits: number[];
    /** The digit the walk landed on out of order, if any. */
    violationDigit: number | null;
    /** The digit that should have come instead of `violationDigit`. */
    expectedDigit: number | null;
    /** The walk returned to the ramp it started from. */
    closed: boolean;
    /** Why the walk stopped short: the ramp whose jump went nowhere / crashed. */
    deadEndRampId: number | null;
}

export class JumpsGame {
    private grid: Grid;
    private ramps: Map<number, Ramp> = new Map();
    private nextRampId: number = 1;
    private currentPath: Position[] = [];
    private digitPositions: Map<number, Position> = new Map();

    protected get gs() { return this.grid.serializer; }

    constructor(grid: Grid) {
        this.grid = grid;
        for (const [digit, pos] of this.grid.getAllDigitPositions()) {
            this.digitPositions.set(digit, pos);
        }
    }

    getGrid(): Grid { return this.grid; }
    getRamps(): Map<number, Ramp> { return this.ramps; }
    getCurrentPath(): Position[] { return this.currentPath; }
    getDigitPositions(): Map<number, Position> { return this.digitPositions; }
    isStarted(): boolean { return this.ramps.size > 0; }

    getLanding(ramp: Ramp): Position { return ramp.path[0]; }
    getLauncher(ramp: Ramp): Position { return ramp.path[ramp.path.length - 1]; }

    // ── Quarters ────────────────────────────────────────────────────────────

    /** Half the grid size — the jump distance, and the width of a quarter. */
    get half(): number { return this.grid.size / 2; }

    /** Quarters only exist on an even grid. Odd grids cannot play Jumps. */
    get hasQuarters(): boolean { return this.grid.size % 2 === 0; }

    quarterOf(pos: Position): Quarter {
        const h = this.half;
        return { qr: pos.row < h ? 0 : 1, qc: pos.col < h ? 0 : 1 };
    }

    sameQuarter(a: Position, b: Position): boolean {
        const qa = this.quarterOf(a), qb = this.quarterOf(b);
        return qa.qr === qb.qr && qa.qc === qb.qc;
    }

    /**
     * Directions whose half-grid translation stays on the board — one horizontal and
     * one vertical per quarter. A jump can never reach the diagonal quarter.
     */
    quarterJumpDirections(pos: Position): Direction[] {
        const q = this.quarterOf(pos);
        const horizontal: Direction = q.qc === 0 ? 'E' : 'W';
        const vertical: Direction = q.qr === 0 ? 'S' : 'N';
        return ALL_DIRECTIONS.filter(d => d === horizontal || d === vertical);
    }

    /**
     * Where a jump from `pos` lands: a pure translation by half the grid, which is what
     * "the same position in the pointed quarter" means. Null if it leaves the board.
     */
    jumpTarget(pos: Position, dir: Direction): Position | null {
        const h = this.half;
        const target: Position = {
            row: pos.row + (dir === 'S' ? h : dir === 'N' ? -h : 0),
            col: pos.col + (dir === 'E' ? h : dir === 'W' ? -h : 0)
        };
        return this.grid.isValidPosition(target) ? target : null;
    }

    /** Where this ramp's jumper lands. */
    getRampTarget(ramp: Ramp): Position | null {
        return this.jumpTarget(this.getLauncher(ramp), ramp.jumpDirection);
    }

    // ── Jump direction choice ───────────────────────────────────────────────

    /** Direction the ramp body runs as it enters the launcher. */
    private getExitDirection(ramp: Ramp): Direction | null {
        if (ramp.path.length < 2) return null;
        const last = ramp.path[ramp.path.length - 1];
        const prev = ramp.path[ramp.path.length - 2];
        if (prev.row < last.row) return 'S';
        if (prev.row > last.row) return 'N';
        if (prev.col < last.col) return 'E';
        return 'W';
    }

    /**
     * The launcher's legal choices: quarter-legal, minus pointing back along its own
     * body. Only one of the two quarter directions can ever be blocked, so this is
     * never empty.
     */
    getValidJumpDirections(ramp: Ramp): Direction[] {
        const launcher = this.getLauncher(ramp);
        const dirs = this.quarterJumpDirections(launcher);
        const exit = this.getExitDirection(ramp);
        const blocked = exit ? oppositeDir(exit) : null;
        const allowed = dirs.filter(d => d !== blocked);
        return allowed.length > 0 ? allowed : dirs;
    }

    /**
     * The direction to open a freshly placed launcher on. A ramp landing on digit `d`
     * has to hand the jumper on toward `d + 1`, so if one of its legal jumps reaches
     * that digit's cell outright, take it — on a sound build the aim is then already
     * right. Only one jump can reach a given cell (the horizontal and vertical
     * translations always differ), so there's nothing to tie-break.
     *
     * Falls back to the first legal direction when the landing isn't a digit — an
     * intermediate ramp, where which digit comes next isn't known yet — or when
     * neither jump lands on it.
     */
    private bestJumpDirection(ramp: Ramp): Direction {
        const dirs = this.getValidJumpDirections(ramp);
        const digit = this.grid.getDigitAt(this.getLanding(ramp));
        if (digit === null) return dirs[0];

        const next = this.grid.getDigitPosition(this.grid.nextDigit(digit));
        if (!next) return dirs[0];

        const launcher = this.getLauncher(ramp);
        for (const dir of dirs) {
            const target = this.jumpTarget(launcher, dir);
            if (target && positionsEqual(target, next)) return dir;
        }
        return dirs[0];
    }

    /** Aim a launcher at `dir`, when that's one of its legal jumps. */
    setJumpDirection(rampId: number, dir: Direction): boolean {
        const ramp = this.ramps.get(rampId);
        if (!ramp) return false;
        if (!this.getValidJumpDirections(ramp).includes(dir)) return false;
        if (ramp.jumpDirection === dir) return false;
        ramp.jumpDirection = dir;
        return true;
    }

    cycleJumpDirection(rampId: number): boolean {
        const ramp = this.ramps.get(rampId);
        if (!ramp) return false;
        const valid = this.getValidJumpDirections(ramp);
        const i = valid.indexOf(ramp.jumpDirection);
        ramp.jumpDirection = valid[(i + 1) % valid.length];
        return true;
    }

    // ── Building ────────────────────────────────────────────────────────────

    /**
     * The paired cell of an *active* gate on this cell, or null. The base game has no
     * gates; {@link JumpsVortex} overrides it, and every gate rule below is expressed
     * through this one hook.
     */
    protected gatePairFor(_pos: Position): Position | null {
        return null;
    }

    /** On the board, unoccupied, and — unless it is the landing — not a digit. */
    private isFreeCell(pos: Position, isLanding: boolean): boolean {
        if (!this.hasQuarters) return false;
        if (!this.grid.isValidPosition(pos)) return false;
        if (!isLanding && this.grid.hasDigitAt(pos)) return false;
        if (this.getRampAtCell(pos)) return false;
        if (this.currentPath.some(p => positionsEqual(p, pos))) return false;
        return true;
    }

    /**
     * Whether a cell may join a ramp as an ordinary step. Only the landing (`isLanding`)
     * may sit on a digit; every other cell must be empty. An active gate is never an
     * ordinary step — it joins a ramp only as an explicit teleport (see {@link extendRamp}),
     * which is also what stops a straight run from silently sliding over one.
     */
    protected isValidRampCell(pos: Position, isLanding: boolean): boolean {
        if (this.gatePairFor(pos)) return false;
        return this.isFreeCell(pos, isLanding);
    }

    /** The two cells are an active gate pair, i.e. the jumper teleports between them. */
    isTeleportStep(a: Position, b: Position): boolean {
        const pair = this.gatePairFor(a);
        return pair !== null && positionsEqual(pair, b);
    }

    /**
     * The ramp's path split into contiguous runs, one per quarter it walks in. A plain ramp
     * is a single run; a ramp through a gate breaks into a run either side of the teleport.
     */
    getBodyRuns(ramp: Ramp): Position[][] {
        return this.splitRuns(ramp.path);
    }

    /** The same split, for the ramp currently being built. */
    getBuildingRuns(): Position[][] {
        return this.splitRuns(this.currentPath);
    }

    private splitRuns(path: Position[]): Position[][] {
        if (path.length === 0) return [];
        const runs: Position[][] = [];
        let run: Position[] = [path[0]];
        for (let i = 1; i < path.length; i++) {
            if (this.isTeleportStep(path[i - 1], path[i])) {
                runs.push(run);
                run = [];
            }
            run.push(path[i]);
        }
        runs.push(run);
        return runs;
    }

    private isAligned(a: Position, b: Position): boolean {
        return a.row === b.row || a.col === b.col;
    }

    private getCellsBetween(a: Position, b: Position): Position[] {
        const cells: Position[] = [];
        if (a.row === b.row) {
            const lo = Math.min(a.col, b.col), hi = Math.max(a.col, b.col);
            for (let col = lo + 1; col < hi; col++) cells.push({ row: a.row, col });
        } else if (a.col === b.col) {
            const lo = Math.min(a.row, b.row), hi = Math.max(a.row, b.row);
            for (let row = lo + 1; row < hi; row++) cells.push({ row, col: a.col });
        }
        return cells;
    }

    startRamp(pos: Position): boolean {
        if (this.currentPath.length > 0) return false;
        if (!this.isValidRampCell(pos, true)) return false;
        this.currentPath = [{ ...pos }];
        return true;
    }

    extendRamp(pos: Position): boolean {
        if (this.currentPath.length === 0) return false;

        const last = this.currentPath[this.currentPath.length - 1];
        if (positionsEqual(last, pos)) return false;
        if (!this.isAligned(last, pos)) return false;

        // A ramp never crosses a boundary line. Checking against the last cell — rather
        // than the landing — is what lets a gated ramp carry on in the paired quarter; a
        // quarter is a rectangle and the run is straight, so the cells between follow.
        if (!this.sameQuarter(last, pos)) return false;

        const between = this.getCellsBetween(last, pos);
        for (const cell of between) {
            if (!this.isValidRampCell(cell, false)) return false;
        }

        // Clicking an active gate steps onto it *and* out of its pair, in one move.
        const pair = this.gatePairFor(pos);
        if (pair) {
            if (!this.isFreeCell(pos, false) || !this.isFreeCell(pair, false)) return false;
            for (const cell of between) this.currentPath.push({ ...cell });
            this.currentPath.push({ ...pos });
            this.currentPath.push({ ...pair });
            return true;
        }

        if (!this.isValidRampCell(pos, false)) return false;

        for (const cell of between) this.currentPath.push({ ...cell });
        this.currentPath.push({ ...pos });
        return true;
    }

    finishRamp(): boolean {
        if (this.currentPath.length < 2) return false;
        // A gate is something the jumper passes through, never where it takes off from.
        if (this.gatePairFor(this.currentPath[this.currentPath.length - 1])) return false;

        const ramp: Ramp = {
            id: this.nextRampId,
            path: [...this.currentPath],
            jumpDirection: 'N'
        };
        ramp.jumpDirection = this.bestJumpDirection(ramp);

        this.ramps.set(ramp.id, ramp);
        this.nextRampId++;
        this.currentPath = [];
        return true;
    }

    cancelRamp(): void { this.currentPath = []; }

    /** Whether a ramp *body* cell may occupy `pos` — the landing is the only part
     *  allowed on a digit, so this always asks the stricter question. */
    canBuildOn(pos: Position): boolean {
        return this.isValidRampCell(pos, false);
    }

    /**
     * Reopen a finished ramp for editing from `pos`: the ramp is removed and the
     * landing-side part of its path — up to and including `pos` — becomes the pending
     * build, so drawing carries on from there and the rest is erased. Reopening at
     * the launcher keeps the whole path; stepping back off it then retracts.
     */
    resumeRamp(pos: Position): boolean {
        const ramp = this.getRampAtCell(pos);
        if (!ramp) return false;
        const idx = ramp.path.findIndex(p => positionsEqual(p, pos));
        if (idx < 0) return false;

        this.ramps.delete(ramp.id);
        this.currentPath = ramp.path.slice(0, idx + 1).map(p => ({ ...p }));
        return true;
    }

    /** Cut the pending build back to `pos`, dropping everything drawn after it.
     *  No-op (false) when `pos` isn't on the build. */
    truncateBuild(pos: Position): boolean {
        const idx = this.currentPath.findIndex(p => positionsEqual(p, pos));
        if (idx < 0) return false;
        this.currentPath = this.currentPath.slice(0, idx + 1);
        return true;
    }

    /** Drop the last cell of the pending build — tracing back over your own path
     *  erases it. Keeps at least the landing. */
    retractBuild(): boolean {
        if (this.currentPath.length < 2) return false;
        this.currentPath.pop();
        return true;
    }

    /** Every step of the path is either an orthogonal move or a live teleport. */
    private isPathJoined(path: Position[]): boolean {
        for (let i = 1; i < path.length; i++) {
            const a = path[i - 1], b = path[i];
            if (manhattanDistance(a, b) === 1) continue;
            if (this.isTeleportStep(a, b)) continue;
            return false;
        }
        return true;
    }

    /**
     * Drop ramps whose path no longer joins up. Switching a gate off severs any ramp that
     * was routed through it, and a ramp in two disconnected pieces is not a thing the
     * jumper can walk — so it goes, rather than lingering as a broken board state.
     */
    protected pruneDisconnectedRamps(): void {
        for (const [id, ramp] of [...this.ramps]) {
            if (!this.isPathJoined(ramp.path)) this.ramps.delete(id);
        }
        if (!this.isPathJoined(this.currentPath)) this.cancelRamp();
    }

    removeRamp(id: number): boolean { return this.ramps.delete(id); }

    getRampAtCell(pos: Position): Ramp | null {
        for (const [, ramp] of this.ramps) {
            if (ramp.path.some(c => positionsEqual(pos, c))) return ramp;
        }
        return null;
    }

    /** The ramp whose landing is on this cell, if any. */
    getRampLandingAt(pos: Position): Ramp | null {
        for (const [, ramp] of this.ramps) {
            if (positionsEqual(this.getLanding(ramp), pos)) return ramp;
        }
        return null;
    }

    isAtBuildingStart(pos: Position): boolean {
        if (this.currentPath.length === 0) return false;
        return positionsEqual(pos, this.currentPath[0]);
    }

    isAtBuildingEnd(pos: Position): boolean {
        if (this.currentPath.length < 2) return false;
        return positionsEqual(pos, this.currentPath[this.currentPath.length - 1]);
    }

    /** Click FSM, shared by input and the tutorial adapter. Returns true if state changed. */
    handleClick(pos: Position): boolean {
        if (this.currentPath.length === 0) {
            const existing = this.getRampAtCell(pos);
            if (existing) {
                if (positionsEqual(pos, this.getLanding(existing))) {
                    this.removeRamp(existing.id);
                    return true;
                }
                if (positionsEqual(pos, this.getLauncher(existing))) {
                    return this.cycleJumpDirection(existing.id);
                }
                return false;  // body click, no action
            }
            return this.startRamp(pos);
        }

        if (this.isAtBuildingStart(pos)) {
            this.cancelRamp();
            return true;
        }
        if (this.isAtBuildingEnd(pos)) return this.finishRamp();
        return this.extendRamp(pos);
    }

    // ── The jumper's walk ───────────────────────────────────────────────────

    /** The ramp this one's jumper reaches, or null if the jump lands on no landing. */
    getTargetRamp(ramp: Ramp): Ramp | null {
        const target = this.getRampTarget(ramp);
        if (!target) return null;
        return this.getRampLandingAt(target);
    }

    /**
     * A jump that lands on another ramp's body or launcher rather than its landing —
     * the jumper crashes into the ramp mid-length.
     */
    isCrash(ramp: Ramp): boolean {
        const target = this.getRampTarget(ramp);
        if (!target) return false;
        const hit = this.getRampAtCell(target);
        return hit !== null && !positionsEqual(this.getLanding(hit), target);
    }

    /**
     * Walk the jumper from the landing on the lowest digit, following jumps until it
     * closes, dead-ends, or loops back on itself. This is what the win and the status bar
     * are built on; the *board's* colours are local per-ramp and come from
     * {@link computeRampColors} instead.
     */
    computeChain(): JumpChain {
        const chain: JumpChain = {
            visited: [], clearedDigits: [],
            violationDigit: null, expectedDigit: null, closed: false, deadEndRampId: null
        };

        const minPos = this.digitPositions.get(this.grid.minDigit);
        if (!minPos) return chain;
        const start = this.getRampLandingAt(minPos);
        if (!start) return chain;

        let inOrder = true;
        let expected = this.grid.nextDigit(this.grid.minDigit);
        chain.clearedDigits.push(this.grid.minDigit);

        let current: Ramp = start;
        const seen = new Set<number>();
        while (true) {
            chain.visited.push(current.id);
            seen.add(current.id);

            const next = this.getTargetRamp(current);
            if (!next) { chain.deadEndRampId = current.id; break; }
            if (next.id === start.id) { chain.closed = true; break; }
            if (seen.has(next.id)) break;  // a sub-loop that excludes the start

            const digit = this.grid.getDigitAt(this.getLanding(next));
            if (digit !== null && inOrder) {
                if (digit === expected) {
                    chain.clearedDigits.push(digit);
                    expected = this.grid.nextDigit(digit);
                } else {
                    inOrder = false;
                    chain.violationDigit = digit;
                    chain.expectedDigit = expected;
                }
            }
            current = next;
        }
        return chain;
    }

    // ── Colours ─────────────────────────────────────────────────────────────

    /**
     * Per-ramp colour: **amber by default, teal once the ramp is part of a completed run
     * from one digit to the next**.
     *
     * A run starts at a ramp landing on digit *d* and follows jumps forward. Landings on
     * empty cells are pure routing, so the run carries on through them; the first digit it
     * reaches decides the outcome — `nextDigit(d)` turns the whole run teal, anything else
     * leaves it amber. A direct digit-to-digit jump is just a run of length one.
     *
     * Deliberately **local**: each link is judged on its own, so a 5 → 6 run reads as
     * correct without digits 1-4 being wired up yet.
     */
    computeRampColors(): Map<number, JumpColor> {
        const colors = new Map<number, JumpColor>();
        for (const [, ramp] of this.ramps) colors.set(ramp.id, 'amber');

        for (const [, start] of this.ramps) {
            const digit = this.grid.getDigitAt(this.getLanding(start));
            if (digit === null) continue;
            const wanted = this.grid.nextDigit(digit);

            const run = [start.id];
            const seen = new Set<number>([start.id]);
            let current = start;
            while (true) {
                const target = this.getRampTarget(current);
                if (!target) break;

                // Reaching the digit's *cell* is what counts — that digit's own ramp need
                // not exist yet, so a link reads as correct as soon as it is aimed right.
                const reached = this.grid.getDigitAt(target);
                if (reached !== null) {
                    if (reached === wanted) for (const id of run) colors.set(id, 'teal');
                    break;
                }

                const next = this.getRampLandingAt(target);
                if (!next || seen.has(next.id)) break;
                run.push(next.id);
                seen.add(next.id);
                current = next;
            }
        }
        return colors;
    }

    getRampColor(ramp: Ramp, colors = this.computeRampColors()): JumpColor {
        return colors.get(ramp.id) ?? 'amber';
    }

    getLandingColor(ramp: Ramp, colors = this.computeRampColors()): JumpColor {
        return this.getRampColor(ramp, colors);
    }

    /** As the ramp, but red when the jump has nowhere valid to come down. */
    getLauncherColor(ramp: Ramp, colors = this.computeRampColors()): JumpColor {
        if (this.isCrash(ramp)) return 'red';
        return this.getRampColor(ramp, colors);
    }

    /** Cells two or more launchers jump into. */
    getDoubleTargetedCells(): Position[] {
        const counts = new Map<string, { pos: Position; n: number }>();
        for (const [, ramp] of this.ramps) {
            const t = this.getRampTarget(ramp);
            if (!t) continue;
            const key = positionKey(t);
            const entry = counts.get(key);
            if (entry) entry.n++;
            else counts.set(key, { pos: t, n: 1 });
        }
        return [...counts.values()].filter(e => e.n >= 2).map(e => e.pos);
    }

    /** Cells where a jump crashes into a ramp body or launcher. */
    getCrashCells(): Position[] {
        const cells: Position[] = [];
        for (const [, ramp] of this.ramps) {
            if (!this.isCrash(ramp)) continue;
            const t = this.getRampTarget(ramp);
            if (t) cells.push(t);
        }
        return cells;
    }

    /** Digits with no landing on them yet, ascending. */
    getUnlandedDigits(): number[] {
        const missing: number[] = [];
        for (const [digit, pos] of this.digitPositions) {
            if (!this.getRampLandingAt(pos)) missing.push(digit);
        }
        return missing.sort((a, b) => a - b);
    }

    // ── Win & score ─────────────────────────────────────────────────────────

    /**
     * One cycle through every ramp, landing on every digit in order. A closed walk
     * covering all ramps is necessarily a permutation, so no landing can be targeted
     * twice — that case shows up as ramps the walk never reaches.
     */
    checkWin(): boolean {
        if (this.ramps.size === 0) return false;
        if (this.getUnlandedDigits().length > 0) return false;
        const chain = this.computeChain();
        return chain.closed
            && chain.visited.length === this.ramps.size
            && chain.clearedDigits.length === this.grid.digitCount;
    }

    /**
     * Ramp cells that are neither landing nor launcher — the body a ramp needs to walk the
     * jumper across its quarter. A two-cell ramp has none.
     */
    getRampBodyCells(): Position[] {
        const cells = new Map<string, Position>();
        for (const [, ramp] of this.ramps) {
            for (const c of ramp.path.slice(1, -1)) cells.set(positionKey(c), { ...c });
        }
        return [...cells.values()];
    }

    /** One launcher per ramp, then the body cells as tiebreak. */
    getScore(): ScoreBreakdown | null {
        if (!this.checkWin()) return null;
        return calculateScore(this.ramps.size, this.getRampBodyCells().length);
    }

    /** Scoring overlay: body cells (secondary). The launchers (primary) are redrawn amber. */
    getScoringHighlight(): ScoringHighlight {
        return { secondaryCells: this.getRampBodyCells() };
    }

    reset(): void {
        this.ramps.clear();
        this.currentPath = [];
        this.nextRampId = 1;
    }

    // ── Status ──────────────────────────────────────────────────────────────

    getStatus(): string {
        const boldRed = (text: string) =>
            `<span style="color:#e94560;font-weight:bold">${text}</span>`;

        if (!this.hasQuarters) {
            return `Jumps needs an even-sized grid — this ${this.grid.size}×${this.grid.size} grid has no quarters.`;
        }

        if (this.checkWin()) {
            return `You win! One cycle of jumps through digits ${this.grid.minDigit}-${this.grid.maxDigit}!`;
        }

        if (this.currentPath.length > 0) {
            if (this.currentPath.length < 2) {
                return 'Building ramp: click a cell in line with the landing to extend it.';
            }
            return `Building ramp: ${this.currentPath.length} cells. Click the end to place the launcher.`;
        }

        if (this.ramps.size === 0) {
            return 'Click a digit to start its ramp, extend it inside the quarter, then click the end to launch.';
        }

        // ── Red conditions first ──
        if (this.getCrashCells().length > 0) {
            return `${boldRed('A jump crashes into a ramp')} — it must land on a landing circle.`;
        }
        if (this.getDoubleTargetedCells().length > 0) {
            return `${boldRed('Two launchers jump onto the same landing')} — re-aim one of them.`;
        }

        const chain = this.computeChain();
        if (chain.violationDigit !== null) {
            return `${boldRed(`The jumper lands on digit ${chain.violationDigit}`)} before digit ${chain.expectedDigit} — reroute so the digits come up in order.`;
        }

        // ── No red: guide the next step ──
        const missing = this.getUnlandedDigits();
        if (missing.length > 0) {
            return `Digit ${missing[0]} has no landing yet — start a ramp on it.`;
        }

        const stray = [...this.ramps.values()].filter(r => !this.getTargetRamp(r));
        if (stray.length > 0) {
            return `${stray.length} jump${stray.length > 1 ? 's' : ''} land on empty cells — aim each launcher at a landing, or add a ramp there.`;
        }

        const unreached = this.ramps.size - chain.visited.length;
        if (unreached > 0) {
            return `${unreached} ramp${unreached > 1 ? 's' : ''} the jumper never reaches — every ramp must be part of the single cycle.`;
        }

        return `Close the cycle: the last jump must land back on digit ${this.grid.minDigit}.`;
    }

    // ── Serialization ───────────────────────────────────────────────────────

    /** `A0A1A2E` per ramp, concatenated: landing-first path, then the jump direction. */
    serialize(): string {
        const entries: string[] = [];
        for (const [, ramp] of this.ramps) {
            entries.push(ramp.path.map(p => this.gs.serializePosition(p)).join('') + ramp.jumpDirection);
        }
        return entries.join('');
    }

    deserialize(serialized: string): void {
        this.reset();
        if (!serialized) return;

        const compact = stripSeparators(serialized);
        const lastCol = String.fromCharCode('A'.charCodeAt(0) + this.grid.size - 1);
        const re = new RegExp(`([A-${lastCol}][0-${this.grid.size - 1}]){2,}[NSEW]`, 'g');

        let m: RegExpExecArray | null;
        while ((m = re.exec(compact)) !== null) {
            const token = m[0];
            const direction = token[token.length - 1] as Direction;

            const posStr = token.slice(0, -1);
            const path: Position[] = [];
            for (let i = 0; i < posStr.length; i += 2) {
                try {
                    path.push(this.gs.deserializePosition(posStr.slice(i, i + 2)));
                } catch {
                    break;
                }
            }
            if (path.length < 2) continue;

            this.ramps.set(this.nextRampId, { id: this.nextRampId, path, jumpDirection: direction });
            this.nextRampId++;
        }
    }
}
